import React from 'react';
import { Box, Typography } from '@mui/material';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';
import PanelBlock, { PanelEyebrow, Prose } from './PanelBlock';
import type { StructuredWorkup } from '../../types/laboklin';

/**
 * What the report means, as the assistant wrote it.
 *
 * The verdict is set against the question the veterinarian actually asked the
 * laboratory, because that is what they will read first and it is the one thing the
 * report itself never answers. Everything below it is the assistant's own prose,
 * split into the lines it was already written as; nothing is summarised here.
 */

interface InterpretationPanelProps {
  workup: StructuredWorkup;
  sectionRefs: React.MutableRefObject<Map<string, HTMLElement | null>>;
  onSectionHover: (analyteIds: string[] | null) => void;
}


export const InterpretationPanel: React.FC<InterpretationPanelProps> = ({
  workup,
  sectionRefs,
  onSectionHover,
}) => {
  const { question, verdict, sections, limits } = workup;

  if (!verdict && sections.length === 0 && !limits) return null;

  return (
    <PanelBlock>
      <PanelEyebrow label="Interpretation" state="From the report's own values" />

      {verdict && (
        <Box
          sx={{
            borderLeft: `3px solid ${LABOKLIN_RED}`,
            bgcolor: '#fff6f6',
            px: 1.5,
            py: 1,
            mb: 1.5,
          }}
        >
          {question && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.25 }}>
              {question}
            </Typography>
          )}
          <Typography sx={{ fontSize: 14 }} component="div">
            <Prose>{verdict}</Prose>
          </Typography>
        </Box>
      )}

      {sections.map((section) => (
        <Box
          key={section.title}
          ref={(node: HTMLElement | null) => {
            sectionRefs.current.set(section.title, node);
          }}
          onMouseEnter={() => onSectionHover(section.analyteIds)}
          onMouseLeave={() => onSectionHover(null)}
          sx={{ mb: 1.25, borderRadius: 1, transition: 'background-color 200ms' }}
        >
          <Typography sx={{ fontSize: 14 }} component="div">
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {section.title}.
            </Box>{' '}
            <Prose>{section.text}</Prose>
          </Typography>
        </Box>
      ))}

      {limits && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
          <Typography sx={{ fontSize: 14 }} component="div">
            <Box component="strong" sx={{ fontWeight: 600 }}>
              What this panel cannot tell you.
            </Box>{' '}
            <Prose>{limits}</Prose>
          </Typography>
        </Box>
      )}
    </PanelBlock>
  );
};

export default InterpretationPanel;
